import React, { useState, useEffect } from 'react'
import { Save, X, Clock, IndianRupee, CheckCircle, XCircle, CreditCard, ChevronDown, ChevronUp } from 'lucide-react'
import { useData } from '../context/DataContext'
import { handleDecimalChange, DECIMAL_PRESETS } from '../utils/decimalValidation'

function WorkEntryForm({ workEntry, onSubmit, onCancel, isSubmitting = false }) {
  const { state } = useData()
  const { customers, vehicles, loading } = state

  const [formData, setFormData] = useState({
    customerId: '',
    location: '',
    amount: '',
    notes: '',
    initialPayment: '',
    paymentMethod: 'cash',
    paymentNotes: '',
    paymentStatusDescription: '', // New field for payment status description
    agreedAmount: '',
    paidAmount: '',
    discountAmount: '',
    discountReason: '',
    // New fields for GST and invoice
    gstNumber: '33FUJPB2105R1ZL', // Default GST number
    hsnCode: '997313', // Default HSN code
    taxAmount: '',
    invoiceNumber: '',
    serialNumber: '',
    // New field for multiple work items
    workItems: [{ 
      serialNumber: '', 
      description: '', 
      hsnCode: '', 
      ratePerHour: '', 
      amount: '',
      betaAmount: '', // Extra amount collected from customer (not in invoice)
      driverExpenses: '', // Driver expenses (not reflected in calculations)
      workDate: new Date().toISOString().split('T')[0],
      startTime: '',
      endTime: '',
      hoursWorked: '',
      vehicleId: '',
      driverId: ''
    }]
  })

  const [errors, setErrors] = useState({})
  const [availableVehicles, setAvailableVehicles] = useState([])
  const [gstExplicitlyAdded, setGstExplicitlyAdded] = useState(false)
  
  // State for collapsed work items - new items start expanded, existing items start collapsed
  const [collapsedWorkItems, setCollapsedWorkItems] = useState({})

  // Helper function to parse time string to hours
  const parseTimeString = (timeStr) => {
    if (!timeStr) return null
    
    // Remove extra spaces and convert to lowercase
    const cleanTime = timeStr.trim().toLowerCase()
    
    // Handle 12-hour format with AM/PM
    const amPmMatch = cleanTime.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)$/)
    if (amPmMatch) {
      let hours = parseInt(amPmMatch[1])
      const minutes = amPmMatch[2] ? parseInt(amPmMatch[2]) : 0
      const period = amPmMatch[3]
      
      if (period === 'pm' && hours !== 12) hours += 12
      if (period === 'am' && hours === 12) hours = 0
      
      return hours + (minutes / 60)
    }
    
    // Handle decimal numbers (e.g., "240.5", "250.75")
    const decimalMatch = cleanTime.match(/^(\d+\.\d+)$/)
    if (decimalMatch) {
      return parseFloat(decimalMatch[1])
    }
    
    // Handle simple numbers (e.g., "240", "250")
    const numberMatch = cleanTime.match(/^(\d+)$/)
    if (numberMatch) {
      return parseFloat(numberMatch[1])
    }
    
    // Handle 24-hour format (HH:MM) - ONLY if it has a colon
    const timeMatch = cleanTime.match(/^(\d{1,2}):(\d{2})$/)
    if (timeMatch) {
      const hours = parseInt(timeMatch[1])
      const minutes = parseInt(timeMatch[2])
      return hours + (minutes / 60)
    }
    
    return null
  }

  // Helper function to calculate hours difference
  const calculateHoursDifference = (startTime, endTime) => {
    const startHours = parseTimeString(startTime)
    const endHours = parseTimeString(endTime)
    
    if (startHours === null || endHours === null) return null
    
    let diffHours = endHours - startHours
    
    // If start time is greater than end time, return 0 (no hours)
    if (diffHours < 0) {
      return 0 // Return 0 when start time is greater than end time
    }
    
    return diffHours // Return hours directly
  }

  useEffect(() => {
    if (workEntry) {
      // Ensure all fields have defined values to prevent controlled/uncontrolled input warnings
      setFormData({
        customerId: workEntry.customerId || '',
        location: workEntry.location || '',
        amount: workEntry.amount !== null && workEntry.amount !== undefined ? workEntry.amount : '',
        notes: workEntry.notes || '',
        initialPayment: workEntry.initialPayment !== null && workEntry.initialPayment !== undefined ? workEntry.initialPayment : '',
        paymentMethod: workEntry.paymentMethod || 'cash',
        paymentNotes: workEntry.paymentNotes || '',
        paymentStatusDescription: workEntry.paymentStatusDescription || '',
        agreedAmount: workEntry.agreedAmount !== null && workEntry.agreedAmount !== undefined ? workEntry.agreedAmount : '',
        paidAmount: workEntry.paidAmount !== null && workEntry.paidAmount !== undefined ? workEntry.paidAmount : '',
        discountAmount: workEntry.discountAmount !== null && workEntry.discountAmount !== undefined ? workEntry.discountAmount : '',
        discountReason: workEntry.discountReason || '',
        // New fields for GST and invoice
        gstNumber: workEntry.gstNumber || '33FUJPB2105R1ZL', // Default GST number
        hsnCode: workEntry.hsnCode || '997313', // Default HSN code
        taxAmount: workEntry.taxAmount !== null && workEntry.taxAmount !== undefined ? workEntry.taxAmount : '',
        invoiceNumber: workEntry.invoiceNumber || '',
         serialNumber: workEntry.serialNumber || '',
         // New field for multiple work items
         workItems: workEntry.workItems && workEntry.workItems.length > 0 ? workEntry.workItems.map(item => ({
           serialNumber: item.serialNumber || '',
           description: item.description || '',
           hsnCode: item.hsnCode || '997313',
           ratePerHour: item.ratePerHour !== null && item.ratePerHour !== undefined ? item.ratePerHour : '',
           amount: item.amount !== null && item.amount !== undefined ? item.amount : '',
           betaAmount: item.betaAmount !== null && item.betaAmount !== undefined ? item.betaAmount : '',
           driverExpenses: item.driverExpenses !== null && item.driverExpenses !== undefined ? item.driverExpenses : '',
           workDate: item.workDate || new Date().toISOString().split('T')[0],
           startTime: item.startTime || '',
           endTime: item.endTime || '',
           hoursWorked: item.hoursWorked !== null && item.hoursWorked !== undefined ? item.hoursWorked : '',
           vehicleId: item.vehicleId || '',
           driverId: item.driverId || ''
         })) : [{
           serialNumber: workEntry.serialNumber || '',
           description: workEntry.workDescription || '',
           hsnCode: workEntry.hsnCode || '997313',
           ratePerHour: workEntry.ratePerHour !== null && workEntry.ratePerHour !== undefined ? workEntry.ratePerHour : '',
           amount: workEntry.amount !== null && workEntry.amount !== undefined ? workEntry.amount : '',
           betaAmount: workEntry.betaAmount !== null && workEntry.betaAmount !== undefined ? workEntry.betaAmount : '',
           driverExpenses: workEntry.driverExpenses !== null && workEntry.driverExpenses !== undefined ? workEntry.driverExpenses : '',
           workDate: workEntry.workDate || new Date().toISOString().split('T')[0],
           startTime: workEntry.startTime || '',
           endTime: workEntry.endTime || '',
           hoursWorked: workEntry.hoursWorked !== null && workEntry.hoursWorked !== undefined ? workEntry.hoursWorked : '',
           vehicleId: workEntry.vehicleId || '',
           driverId: workEntry.driverId || ''
         }]
      })
      
      // Set GST state based on whether tax amount exists in the entry
      setGstExplicitlyAdded(!!workEntry.taxAmount)
    }
  }, [workEntry])

  // Initialize collapse state for work items - only when work items are added/removed
  useEffect(() => {
    if (formData.workItems && formData.workItems.length > 0) {
      const initialCollapsedState = {}
      formData.workItems.forEach((item, index) => {
        // If editing existing entry (workEntry exists), start collapsed; if new entry, start expanded
        const isExistingEntry = !!workEntry
        initialCollapsedState[index] = isExistingEntry
      })
      setCollapsedWorkItems(initialCollapsedState)
    }
  }, [formData.workItems.length, workEntry]) // Run when work items change or when editing vs new entry

  useEffect(() => {
    if (formData.customerId) {
      // Show only ACTIVE vehicles that are either unassigned or assigned to the selected customer
      const availableForCustomer = vehicles.filter(vehicle => 
        vehicle.status === 'active' && // Only active vehicles
        (!vehicle.assigned_to || vehicle.assigned_to === formData.customerId)
      )
      setAvailableVehicles(availableForCustomer)
      
      // Reset vehicle selection if current vehicle is not available for selected customer
      if (formData.vehicleId && !availableForCustomer.find(v => v.id === formData.vehicleId)) {
        setFormData(prev => ({ ...prev, vehicleId: '' }))
      }
    } else {
      setAvailableVehicles([])
    }
  }, [formData.customerId, vehicles])

  // Auto-recalculate final amount when work items change
  useEffect(() => {
    const totalBaseAmount = formData.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
    const totalBetaAmount = formData.workItems.reduce((sum, item) => sum + parseFloat(item.betaAmount || 0), 0)
    const discount = parseFloat(formData.discountAmount || 0)
    const finalAmount = Math.max(0, totalBaseAmount + totalBetaAmount - discount)
    
    // Only update GST if user has explicitly added it (GST only on base amount after discount, not betta amount)
    const baseAmountAfterDiscount = Math.max(0, totalBaseAmount - discount)
    const newGST = gstExplicitlyAdded ? baseAmountAfterDiscount * 0.18 : 0

    // Keep work items expanded - no auto-collapse based on data entry
    
    setFormData(prev => ({ 
      ...prev, 
      amount: finalAmount.toFixed(2),
      taxAmount: gstExplicitlyAdded ? newGST.toFixed(2) : prev.taxAmount
    }))
  }, [formData.workItems, formData.discountAmount, gstExplicitlyAdded])

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // Handle time fields - convert empty string to null
    let processedValue = value
    const timeFields = ['startTime', 'endTime']
    if (timeFields.includes(name) && value === '') {
      processedValue = null
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }



    // Auto-calculate GST when discount changes (based on total of all work items)
    if (name === 'discountAmount') {
      const totalBaseAmount = formData.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
      const totalBetaAmount = formData.workItems.reduce((sum, item) => sum + parseFloat(item.betaAmount || 0), 0)
      const discount = parseFloat(value) || 0
      const finalAmount = Math.max(0, totalBaseAmount + totalBetaAmount - discount)
        
        // Update GST if user has explicitly added it (GST on base amount + betta amount after discount)
        const totalAmountForGST = totalBaseAmount + totalBetaAmount
        const amountAfterDiscount = Math.max(0, totalAmountForGST - discount)
        const newGST = gstExplicitlyAdded ? amountAfterDiscount * 0.18 : 0
        
        setFormData(prev => ({ 
          ...prev, 
          amount: finalAmount.toFixed(2),
          taxAmount: gstExplicitlyAdded ? newGST.toFixed(2) : ''
        }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    console.log('Validating form...')
    console.log('Customer ID:', formData.customerId)

    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required'
      console.log('Customer validation failed')
    }



    // Validate work items
    if (formData.workItems && formData.workItems.length > 0) {
      formData.workItems.forEach((item, index) => {
        if (!item.workDate) {
          newErrors[`workItem${index}Date`] = `Work date is required for work item ${index + 1}`
        }
                 if (!item.hoursWorked || parseFloat(item.hoursWorked) <= 0) {
           newErrors[`workItem${index}Hours`] = `Valid hours worked is required for work item ${index + 1}`
         }
         if (!item.vehicleId) {
           newErrors[`workItem${index}Vehicle`] = `Vehicle is required for work item ${index + 1}`
         }
         if (!item.driverId) {
           newErrors[`workItem${index}Driver`] = `Driver is required for work item ${index + 1}`
         }
        if (!item.serialNumber.trim()) {
          newErrors[`workItem${index}Serial`] = `Serial number is required for work item ${index + 1}`
        }
        if (!item.description.trim()) {
          newErrors[`workItem${index}Description`] = `Description is required for work item ${index + 1}`
        }
        if (!item.ratePerHour || parseFloat(item.ratePerHour) <= 0) {
          newErrors[`workItem${index}Rate`] = `Rate per hour is required for work item ${index + 1}`
        }
      })
    }



    if (!formData.invoiceNumber.trim()) {
      newErrors.invoiceNumber = 'Invoice number is required'
    }



    // Validate that at least one work item has a rate
    const hasValidRate = formData.workItems.some(item => parseFloat(item.ratePerHour || 0) > 0)
    if (!hasValidRate) {
      newErrors.workItems = 'At least one work item must have a valid rate per hour'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    console.log('Form submitted, validating...')
    console.log('Current form data:', formData)
    
    if (validateForm()) {
      // Clean up the data before submitting
      const cleanedData = { ...formData }
      
      // Remove empty strings and convert to null for optional fields
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') {
          cleanedData[key] = null
        }
      })
      
      // Ensure numeric fields are properly handled
      const numericFields = ['hoursWorked', 'ratePerHour', 'amount', 'initialPayment', 'agreedAmount', 'paidAmount', 'discountAmount', 'taxAmount']
      numericFields.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === null) {
          cleanedData[field] = null
        } else if (cleanedData[field]) {
          // Convert to number if it's a valid numeric string
          const numValue = parseFloat(cleanedData[field])
          if (!isNaN(numValue)) {
            cleanedData[field] = numValue
          }
        }
      })
      
      // Ensure time fields are properly handled
      const timeFields = ['startTime', 'endTime']
      timeFields.forEach(field => {
        if (cleanedData[field] === '' || cleanedData[field] === null) {
          cleanedData[field] = null
        }
      })
      
      // Calculate total hours and average rate from work items for database compatibility
      console.log('Work items before calculation:', cleanedData.workItems)
      
      let totalHours, totalAmount, averageRate, driverId
      
      if (cleanedData.workItems.length === 1) {
        // If only 1 work item, use that item's hours and rate directly
        const singleItem = cleanedData.workItems[0]
        totalHours = parseFloat(singleItem.hoursWorked || 0)
        totalAmount = parseFloat(singleItem.amount || 0) + parseFloat(singleItem.betaAmount || 0)
        averageRate = parseFloat(singleItem.ratePerHour || 0)
        driverId = singleItem.driverId || null
        
        console.log(`Single work item: hours = ${singleItem.hoursWorked}, amount = ${singleItem.amount}, rate = ${singleItem.ratePerHour}, driver = ${singleItem.driverId}`)
        console.log(`Using single item values: totalHours = ${totalHours}, totalAmount = ${totalAmount}, averageRate = ${averageRate}, driverId = ${driverId}`)
      } else {
        // If multiple work items, sum up all hours and amounts, calculate average rate
        totalHours = cleanedData.workItems.reduce((sum, item) => {
          const itemHours = parseFloat(item.hoursWorked || 0)
          console.log(`Item ${item.serialNumber}: hours = ${item.hoursWorked}, parsed = ${itemHours}`)
          return sum + itemHours
        }, 0)
        
        totalAmount = cleanedData.workItems.reduce((sum, item) => {
          const itemAmount = parseFloat(item.amount || 0)
          const itemBetaAmount = parseFloat(item.betaAmount || 0)
          const totalItemAmount = itemAmount + itemBetaAmount
          console.log(`Item ${item.serialNumber}: amount = ${item.amount}, betaAmount = ${item.betaAmount}, total = ${totalItemAmount}`)
          return sum + totalItemAmount
        }, 0)
        
        averageRate = totalHours > 0 ? totalAmount / totalHours : 0
        
        // For multiple work items, use the driver from the first work item
        driverId = cleanedData.workItems[0]?.driverId || null
        
        console.log(`Multiple work items: totalHours = ${totalHours}, totalAmount = ${totalAmount}, averageRate = ${averageRate}, driverId = ${driverId}`)
      }
      
      // Add calculated fields for database compatibility
      cleanedData.hoursWorked = totalHours
      cleanedData.ratePerHour = averageRate
      cleanedData.driverId = driverId
      
      // Set work_date from the first work item's date (required by database)
      if (cleanedData.workItems && cleanedData.workItems.length > 0) {
        cleanedData.workDate = cleanedData.workItems[0].workDate
      } else {
        // Fallback to current date if no work items
        cleanedData.workDate = new Date().toISOString().split('T')[0]
      }
      
      console.log('Submitting work entry data:', cleanedData)
      console.log('Tax amount being sent:', cleanedData.taxAmount)
      console.log('Total hours calculated:', totalHours)
      console.log('Average rate calculated:', averageRate)
      console.log('Work date set to:', cleanedData.workDate)
      console.log('Work items after calculation:', cleanedData.workItems)
      onSubmit(cleanedData)
    }
  }

     return (
     <div className="max-w-7xl mx-auto p-6" style={{
       transform: 'translateZ(0)',
       backfaceVisibility: 'hidden',
       perspective: '1000px',
       isolation: 'isolate'
     }}>
       {/* Validation Summary */}

      
      <form 
        onSubmit={handleSubmit} 
        className="space-y-6"
        style={{ 
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          position: 'relative',
          zIndex: 1
        }}
      >
      {/* Customer Selection */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          {/* Customer */}
          <div>
            <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">
              Customer *
            </label>
            <select
              id="customerId"
              name="customerId"
              value={formData.customerId}
              onChange={handleChange}
              className={`input ${errors.customerId ? 'border-red-500 focus:ring-red-500' : ''}`}
            >
              <option value="">Select customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} - {customer.company}
                </option>
              ))}
            </select>
            {errors.customerId && <p className="text-red-500 text-xs mt-1">{errors.customerId}</p>}
          </div>


        </div>
          </div>



      {/* Work Information */}
           <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              Work Location
             </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
               onChange={handleChange}
              className="input"
              placeholder="Enter work location"
            />
          </div>
        </div>

                 {/* Multiple Work Items */}
         <div className="mt-6">
           <div className="flex items-center justify-between mb-4">
             <label className="block text-lg font-semibold text-gray-900">
               Work Items *
             </label>
             <button
               type="button"
                               onClick={() => {
                 const newIndex = formData.workItems.length
                  setFormData(prev => ({
                    ...prev,
                    workItems: [...prev.workItems, { 
                      serialNumber: '', 
                      description: '', 
                      hsnCode: '', 
                      ratePerHour: '', 
                      amount: '',
                      betaAmount: '',
                      driverExpenses: '',
                      workDate: new Date().toISOString().split('T')[0],
                      startTime: '',
                      endTime: '',
                      hoursWorked: '',
                      vehicleId: '',
                      driverId: ''
                    }]
                  }))
                 // Ensure new work item starts expanded
                 setCollapsedWorkItems(prev => ({
                   ...prev,
                   [newIndex]: false
                 }))
                }}
               className="btn btn-primary text-sm px-4 py-2"
             >
               + Add Item
             </button>
              </div>
           
           <div className="space-y-4">
             {formData.workItems.map((item, index) => (
                               <div key={index} className={`card border-2 border-primary-100 bg-gradient-to-br from-primary-50 to-white shadow-md ${collapsedWorkItems[index] ? 'p-3' : 'p-6'}`}>
                  <div className={`flex items-center justify-between ${collapsedWorkItems[index] ? 'mb-0' : 'mb-4'}`}>
                   <div className="flex items-center space-x-2">
                     <button
                       type="button"
                       onClick={() => {
                         setCollapsedWorkItems(prev => ({
                           ...prev,
                           [index]: !prev[index]
                         }))
                       }}
                       className="text-gray-500 hover:text-gray-700 transition-colors"
                       title={collapsedWorkItems[index] ? "Expand work item" : "Collapse work item"}
                     >
                       {collapsedWorkItems[index] ? (
                         <ChevronDown className="h-5 w-5" />
                       ) : (
                         <ChevronUp className="h-5 w-5" />
                       )}
                     </button>
                   <h5 className="font-semibold text-primary-800 text-lg">Work Item {index + 1}</h5>
                   </div>
                   {formData.workItems.length > 1 && (
                     <button
                       type="button"
                       onClick={() => {
                         setFormData(prev => ({
                           ...prev,
                           workItems: prev.workItems.filter((_, i) => i !== index)
                         }))
                       }}
                       className="text-red-600 hover:text-red-800 text-sm font-medium hover:bg-red-50 px-2 py-1 rounded transition-colors"
                     >
                       Remove
                     </button>
                   )}
      </div>

                 {/* Date, Time, Vehicle & Driver Section for this work item */}
                 {!collapsedWorkItems[index] && (
                   <>
                 <div className="mt-4">

                   
                   {/* First Row - Date and Time Fields */}
                   <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          {/* Work Date */}
          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">
              Work Date *
            </label>
            <input
              type="date"
              value={item.workDate || ''}
              onChange={(e) => {
                const newWorkItems = [...formData.workItems]
                newWorkItems[index].workDate = e.target.value
                setFormData(prev => ({ ...prev, workItems: newWorkItems }))
              }}
              className={`input text-sm focus:ring-primary-500 focus:border-primary-500 ${errors[`workItem${index}Date`] ? 'border-red-500 focus:ring-red-500' : ''}`}
            />
            {errors[`workItem${index}Date`] && <p className="text-red-500 text-xs mt-1">{errors[`workItem${index}Date`]}</p>}
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">
              Start Time
            </label>
            <input
              type="text"
              value={item.startTime || ''}
              onChange={(e) => {
                const newWorkItems = [...formData.workItems]
                newWorkItems[index].startTime = e.target.value
                // Auto-calculate hours worked
                if (newWorkItems[index].startTime && newWorkItems[index].endTime) {
                  const hoursDifference = calculateHoursDifference(newWorkItems[index].startTime, newWorkItems[index].endTime)
                  if (hoursDifference !== null) {
                    newWorkItems[index].hoursWorked = hoursDifference.toFixed(2)
                    // Recalculate amount
                    const rate = parseFloat(newWorkItems[index].ratePerHour || 0)
                    newWorkItems[index].amount = (hoursDifference * rate).toFixed(2)
                  } else {
                    // Clear hours and amount if calculation fails
                    newWorkItems[index].hoursWorked = ''
                    newWorkItems[index].amount = ''
                  }
                }
                setFormData(prev => ({ ...prev, workItems: newWorkItems }))
              }}
              className="input text-sm focus:ring-primary-500 focus:border-primary-500"
              placeholder="vehicle meter hours"
            />
          </div>

          {/* End Time */}
          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">
              End Time
            </label>
            <input
              type="text"
              value={item.endTime || ''}
              onChange={(e) => {
                const newWorkItems = [...formData.workItems]
                newWorkItems[index].endTime = e.target.value
                // Auto-calculate hours worked
                if (newWorkItems[index].startTime && newWorkItems[index].endTime) {
                  const hoursDifference = calculateHoursDifference(newWorkItems[index].startTime, newWorkItems[index].endTime)
                  if (hoursDifference !== null) {
                    newWorkItems[index].hoursWorked = hoursDifference.toFixed(2)
                    // Recalculate amount
                    const rate = parseFloat(newWorkItems[index].ratePerHour || 0)
                    newWorkItems[index].amount = (hoursDifference * rate).toFixed(2)
                  } else {
                    // Clear hours and amount if calculation fails
                    newWorkItems[index].hoursWorked = ''
                    newWorkItems[index].amount = ''
                  }
                }
                setFormData(prev => ({ ...prev, workItems: newWorkItems }))
              }}
              className="input text-sm focus:ring-primary-500 focus:border-primary-500"
              placeholder="vehicle meter hours"
            />
          </div>

          {/* Hours Worked */}
          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">
              Hours Worked *
            </label>
            <input
              type="text"
              value={item.hoursWorked || ''}
              onChange={(e) => {
                const newWorkItems = [...formData.workItems]
                newWorkItems[index].hoursWorked = e.target.value
                // Recalculate amount
                const hours = parseFloat(e.target.value || 0)
                const rate = parseFloat(newWorkItems[index].ratePerHour || 0)
                newWorkItems[index].amount = (hours * rate).toFixed(2)
                setFormData(prev => ({ ...prev, workItems: newWorkItems }))
              }}
              onBlur={(e) => {
                // Auto-calculate if user enters hours manually and rate is available
                const newWorkItems = [...formData.workItems]
                const hours = parseFloat(e.target.value || 0)
                const rate = parseFloat(newWorkItems[index].ratePerHour || 0)
                if (hours > 0 && rate > 0) {
                  newWorkItems[index].amount = (hours * rate).toFixed(2)
                  setFormData(prev => ({ ...prev, workItems: newWorkItems }))
                }
              }}
              className={`input text-sm focus:ring-primary-500 focus:border-primary-500 ${errors[`workItem${index}Hours`] ? 'border-red-500 focus:ring-red-500' : ''} ${item.startTime && item.endTime ? 'bg-gray-50' : ''}`}
              placeholder="0.00"
              readOnly={!!(item.startTime && item.endTime)}
              title={item.startTime && item.endTime ? 'Auto-calculated from start and end times' : ''}
            />
                          {item.startTime && item.endTime && (
                <p className="text-xs text-blue-600 mt-1">Auto calculated</p>
              )}
            {errors[`workItem${index}Hours`] && <p className="text-red-500 text-xs mt-1">{errors[`workItem${index}Hours`]}</p>}
          </div>
                   </div>

                   {/* Second Row - Vehicle and Driver Fields */}
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                       {/* Vehicle Selection */}
          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">
              Vehicle *
            </label>
            <select
              value={item.vehicleId || ''}
              onChange={(e) => {
                const newWorkItems = [...formData.workItems]
                newWorkItems[index].vehicleId = e.target.value
                setFormData(prev => ({ ...prev, workItems: newWorkItems }))
              }}
              className="input text-sm focus:ring-primary-500 focus:border-primary-500"
              disabled={!formData.customerId}
            >
              <option value="">Select vehicle</option>
              {availableVehicles.map(vehicle => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.registrationNumber} - {vehicle.type}
                </option>
              ))}
            </select>
            {!formData.customerId && (
              <p className="text-gray-500 text-xs mt-1">Select customer first</p>
            )}
            {errors[`workItem${index}Vehicle`] && (
              <p className="text-red-600 text-xs mt-1 font-medium">{errors[`workItem${index}Vehicle`]}</p>
            )}
          </div>

                       {/* Driver Selection */}
                       <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">
              Driver *
            </label>
            <select
              value={item.driverId || ''}
              onChange={(e) => {
                const newWorkItems = [...formData.workItems]
                newWorkItems[index].driverId = e.target.value
                setFormData(prev => ({ ...prev, workItems: newWorkItems }))
              }}
              className={`input text-sm ${errors[`workItem${index}Driver`] ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
            >
              <option value="">Select driver</option>
              {state.drivers.filter(driver => driver.status === 'active').map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.name}
                </option>
              ))}
            </select>
            {errors[`workItem${index}Driver`] && (
              <p className="text-red-600 text-xs mt-1 font-medium">{errors[`workItem${index}Driver`]}</p>
            )}
                 </div>
                                    </div>

                 {/* Work Item Details Section */}
                                       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
                   {/* Serial Number */}
                   <div className="flex flex-col h-full min-h-[120px]">
                     <label className="block text-sm font-semibold text-primary-700 mb-2">
                       Serial No *
                     </label>
                     <input
                       type="text"
                       value={item.serialNumber}
                       onChange={(e) => {
                         const newWorkItems = [...formData.workItems]
                         newWorkItems[index].serialNumber = e.target.value
                         setFormData(prev => ({ ...prev, workItems: newWorkItems }))
                       }}
                       className={`input h-10 ${!item.serialNumber.trim() ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
                       placeholder="001"
                     />
                     {!item.serialNumber.trim() && (
                       <p className="text-red-600 text-xs mt-1 font-medium">Serial number is required</p>
                     )}
                     <div className="flex-grow"></div>
                   </div>
                   
                   {/* HSN Code */}
                   <div className="flex flex-col h-full min-h-[120px]">
                     <label className="block text-sm font-semibold text-primary-700 mb-2">
                       HSN Code
                     </label>
                     <select
                       value={item.hsnCode || '997313'}
                       onChange={(e) => {
                         const newWorkItems = [...formData.workItems]
                         newWorkItems[index].hsnCode = e.target.value
                         setFormData(prev => ({ ...prev, workItems: newWorkItems }))
                       }}
                       className="input h-10 focus:ring-primary-500 focus:border-primary-500"
                     >
                       <option value="997313">997313</option>
                       <option value="997319">997319</option>
                     </select>
                     <div className="flex-grow"></div>
                   </div>
                   
                   {/* Rate Per Hour */}
                   <div className="flex flex-col h-full min-h-[120px]">
                     <label className="block text-sm font-semibold text-primary-700 mb-2">
                       Rate/Hour (₹) *
                     </label>
              <input
                type="text"
                inputMode="decimal"
                value={item.ratePerHour || ''}
                onChange={(e) => {
                  const newWorkItems = [...formData.workItems]
                  handleDecimalChange(e, (value) => {
                    newWorkItems[index].ratePerHour = value
                    // Auto-calculate amount for this item
                    const hours = parseFloat(item.hoursWorked || 0)
                    const rate = parseFloat(value || 0)
                    newWorkItems[index].amount = (hours * rate).toFixed(2)
                    setFormData(prev => ({ ...prev, workItems: newWorkItems }))
                  })
                }}
                className={`input h-10 ${errors[`workItem${index}Rate`] ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:border-primary-500'}`}
                placeholder="0.00"

              />
                     {errors[`workItem${index}Rate`] && (
                       <p className="text-red-600 text-xs mt-1 font-medium">{errors[`workItem${index}Rate`]}</p>
                     )}
                     <div className="flex-grow"></div>
          </div>

                   {/* Amount */}
          <div className="flex flex-col h-full min-h-[120px]">
                     <label className="block text-sm font-semibold text-primary-700 mb-2">
                       Amount (₹)
            </label>
            <input
                       type="number"
                       step="0.01"
                       min="0"
                       value={item.amount || ''}
                       onChange={(e) => {
                         const newWorkItems = [...formData.workItems]
                         newWorkItems[index].amount = e.target.value
                         setFormData(prev => ({ ...prev, workItems: newWorkItems }))
                       }}
                       className="input h-10 focus:ring-primary-500 focus:border-primary-500 bg-gray-50"
                       placeholder="Auto-calculated"
                       readOnly
                     />
                     <div className="text-xs text-gray-500 mt-1">Hours × Rate</div>
                     <div className="flex-grow"></div>
        </div>

                   {/* Betta Amount */}
          <div className="flex flex-col h-full min-h-[120px]">
                                         <label className="block text-sm font-semibold text-primary-700 mb-2">
                      Betta Amount ₹
                    </label>
            <input
                       type="number"
                       step="0.01"
                       min="0"
                       value={item.betaAmount || ''}
                       onChange={(e) => {
                         const newWorkItems = [...formData.workItems]
                         newWorkItems[index].betaAmount = e.target.value
                         setFormData(prev => ({ ...prev, workItems: newWorkItems }))
                       }}
                       className="input h-10 focus:ring-primary-500 focus:border-primary-500"
                       placeholder="0.00"
                     />

                     <div className="flex-grow"></div>
        </div>

                   {/* Description */}
                   <div className="md:col-span-2">
                     <label className="block text-sm font-semibold text-primary-700 mb-2">
                       Description *
          </label>
                     <input
                       type="text"
                       value={item.description}
                       onChange={(e) => {
                         const newWorkItems = [...formData.workItems]
                         newWorkItems[index].description = e.target.value
                         setFormData(prev => ({ ...prev, workItems: newWorkItems }))
                       }}
                       className={`input focus:ring-primary-500 focus:border-primary-500 ${errors[`workItem${index}Description`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                       placeholder="e.g., JCB Excavation, Soil Transport"
                     />
                     {errors[`workItem${index}Description`] && <p className="text-red-500 text-xs mt-1">{errors[`workItem${index}Description`]}</p>}
                   </div>
                 </div>

                 {/* Fourth Line - Driver Expenses */}
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4 mt-4">
                   {/* Driver Expenses */}
                   <div className="flex flex-col h-full min-h-[120px]">
                     <label className="block text-sm font-semibold text-primary-700 mb-2">
                       Driver Expenses (₹)
                     </label>
                     <input
                       type="number"
                       step="0.01"
                       min="0"
                       value={item.driverExpenses || ''}
                       onChange={(e) => {
                         const newWorkItems = [...formData.workItems]
                         newWorkItems[index].driverExpenses = e.target.value
                         setFormData(prev => ({ ...prev, workItems: newWorkItems }))
                       }}
                       className="input h-10 focus:ring-primary-500 focus:border-primary-500"
                       placeholder="0.00"
                     />
                     <div className="text-xs text-gray-500 mt-1">Driver expenses (not reflected in calculations)</div>
                     <div className="flex-grow"></div>
                   </div>
                 </div>
               </div>
                   </>
                 )}
               </div>
             ))}
             

        </div>
         </div>
         

      </div>

      {/* Billing Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Total Base Amount (Sum of all work items) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Base Amount (₹)
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toFixed(2)}
                className="input pr-10 bg-gray-50"
                placeholder="Auto-calculated"
                readOnly
              />
              <IndianRupee className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <div className="text-xs text-gray-500 mt-1">Sum of all work items</div>
          </div>

          {/* Total Betta Amount (Sum of all betta amounts) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Betta Amount (₹)
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.workItems.reduce((sum, item) => sum + parseFloat(item.betaAmount || 0), 0).toFixed(2)}
                className="input pr-10 bg-orange-50"
                placeholder="Auto-calculated"
                readOnly
              />
              <IndianRupee className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-orange-400" />
            </div>

          </div>

          {/* Total Hours (Sum of all work items) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Hours
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.workItems.reduce((sum, item) => sum + parseFloat(item.hoursWorked || 0), 0).toFixed(2)}
                className="input pr-10 bg-blue-50"
                placeholder="Auto-calculated"
                readOnly
              />
              <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-400" />
            </div>
            <div className="text-xs text-blue-500 mt-1">Sum of all work item hours</div>
          </div>
        </div>

        {/* Discount Section */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-3">Discount/Reduction (Optional)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Discount Amount */}
            <div>
              <label htmlFor="discountAmount" className="block text-sm font-medium text-blue-700 mb-1">
                Discount Amount (₹)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={formData.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)}
                  id="discountAmount"
                  name="discountAmount"
                  value={formData.discountAmount}
                  onChange={handleChange}
                  className="input pr-10"
                  placeholder="0.00"
                />
                <IndianRupee className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
              <div className="text-xs text-blue-600 mt-1">
                Max: ₹{formData.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toLocaleString()}
              </div>
            </div>

            {/* Discount Reason */}
            <div>
              <label htmlFor="discountReason" className="block text-sm font-medium text-blue-700 mb-1">
                Discount Reason
              </label>
              <input
                type="text"
                id="discountReason"
                name="discountReason"
                value={formData.discountReason}
                onChange={handleChange}
                className="input"
                placeholder="e.g., Regular customer, Bulk work, etc."
              />
            </div>
          </div>
        </div>

        {/* Final Amount After Discount */}
        <div className="mt-4">
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
            <span className="flex items-center justify-between">
              <span>Total Payment Amount (₹)</span>
              <span className="text-xs text-gray-500">
                {formData.discountAmount && parseFloat(formData.discountAmount) > 0 ? 
                  `Saved ₹${parseFloat(formData.discountAmount || 0).toLocaleString()}` : 
                  'No discount applied'}
              </span>
            </span>
          </label>
          <div className="relative">
            <input
              type="number"
              step="0.01"
              min="0"
              id="amount"
              name="amount"
              value={Math.max(0, 
                formData.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0) + parseFloat(item.betaAmount || 0), 0) - 
                parseFloat(formData.discountAmount || 0)
              ).toFixed(2)}
              onChange={handleChange}
              className="input pr-10 bg-green-50 border-green-200 text-green-800 font-semibold"
              placeholder="Auto-calculated"
              readOnly
            />
            <IndianRupee className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-600" />
          </div>
          <div className="text-xs text-green-600 mt-1">
            {formData.discountAmount && parseFloat(formData.discountAmount) > 0 ? 
              `Base Amount + Beta Amount - Discount = Total Payment` : 
              'Total payment amount (includes beta amounts for payment tracking)'}

          </div>
        </div>

        {/* GST and Invoice Information */}
        <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <h4 className="font-medium text-purple-900 mb-3">GST & Invoice Details (Optional)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Invoice Number */}
            <div>
              <label htmlFor="invoiceNumber" className="block text-sm font-medium text-purple-700 mb-1">
                Invoice Number *
              </label>
              <input
                type="text"
                id="invoiceNumber"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={handleChange}
                className={`input ${errors.invoiceNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="e.g., INV-2024-001"
              />
              {errors.invoiceNumber && <p className="text-red-500 text-xs mt-1">{errors.invoiceNumber}</p>}
            </div>

            {/* GST Number */}
            <div>
              <label htmlFor="gstNumber" className="block text-sm font-medium text-purple-700 mb-1">
                <span className="flex items-center justify-between">
                  <span>GST Number</span>
                  <span className="text-xs text-purple-500">Pre-filled</span>
                </span>
              </label>
              <input
                type="text"
                id="gstNumber"
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleChange}
                className="input bg-purple-50 border-purple-200 text-purple-800 font-semibold"
                placeholder="33FUJPB2105R1ZL"
                readOnly
              />

            </div>



            {/* Tax Amount */}
            <div>
              <label htmlFor="taxAmount" className="block text-sm font-medium text-purple-700 mb-1">
                <span className="flex items-center justify-between">
                  <span>GST Amount</span>
                </span>
              </label>

              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    id="taxAmount"
                    name="taxAmount"
                    value={formData.taxAmount}
                    onChange={handleChange}
                    className="input pr-10 bg-purple-50 border-purple-200 text-purple-800 font-semibold"
                    placeholder="Auto-calculated"
                    readOnly
                  />
                  <IndianRupee className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-purple-600" />
                </div>
                {!gstExplicitlyAdded ? (
                  <button
                    type="button"
                    onClick={() => {
                      // Calculate GST on base amounts + betta amounts
                      const baseAmount = formData.workItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
                      const betaAmount = formData.workItems.reduce((sum, item) => sum + parseFloat(item.betaAmount || 0), 0)
                      const discount = parseFloat(formData.discountAmount || 0)
                      const totalAmountForGST = baseAmount + betaAmount
                      const amountAfterDiscount = Math.max(0, totalAmountForGST - discount)
                      const gstAmount = amountAfterDiscount * 0.18
                      setFormData(prev => ({ ...prev, taxAmount: gstAmount.toFixed(2) }))
                      setGstExplicitlyAdded(true) // Mark GST as explicitly added
                    }}
                    className="px-3 py-2 text-sm font-medium rounded-lg border bg-green-50 text-green-700 border-green-200 hover:bg-green-100 transition-colors"
                  >
                    Add GST
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, taxAmount: '' }))
                      setGstExplicitlyAdded(false) // Mark GST as not explicitly added
                    }}
                    className="px-3 py-2 text-sm font-medium rounded-lg border bg-red-50 text-red-700 border-red-200 hover:bg-red-100 transition-colors"
                  >
                    Remove GST
                  </button>
                )}
              </div>
              <div className="text-xs text-purple-600 mt-1">
                {formData.taxAmount && parseFloat(formData.taxAmount) > 0 
                  ? 'GST at 18% of final amount' 
                  : 'Click "Add GST" to include 18% tax'
                }
              </div>
            </div>
          </div>

          {/* Total Amount (Including GST) */}
          {formData.taxAmount && parseFloat(formData.taxAmount) > 0 ? (
            <div className="mt-4 p-3 bg-gradient-to-r from-purple-100 to-purple-200 rounded-lg border border-purple-300">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-semibold text-purple-900">Total Amount (Including GST)</h5>
                  <div className="text-sm text-purple-700">
                    Final Amount: ₹{parseFloat(formData.amount || 0).toLocaleString()} + 
                    GST (18%): ₹{parseFloat(formData.taxAmount || 0).toLocaleString()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-900">
                    ₹{(parseFloat(formData.amount || 0) + parseFloat(formData.taxAmount || 0)).toLocaleString()}
                  </div>
                  <div className="text-xs text-purple-600">Final Total</div>
                </div>
              </div>

            </div>
          ) : (
            <div className="mt-4 p-3 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg border border-gray-300">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-semibold text-gray-900">Total Amount (No GST)</h5>
                  <div className="text-sm text-gray-700">
                    Final Amount: ₹{parseFloat(formData.amount || 0).toLocaleString()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">
                    ₹{parseFloat(formData.amount || 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-600">Final Total</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Information */}
      {(
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Status</h3>
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border">
            {/* Manual Payment Entry */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Agreed Amount - Auto from Total Amount (Including GST) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agreed Amount (₹) *
                </label>
                <div className="input text-center text-lg font-semibold bg-blue-50 border-blue-200 text-blue-800">
                  ₹{(parseFloat(formData.amount || 0) + parseFloat(formData.taxAmount || 0)).toLocaleString()}
                </div>
                <div className="text-xs text-blue-600 mt-1 text-center">
                  {formData.taxAmount && parseFloat(formData.taxAmount) > 0 
                    ? 'Final Amount + GST (18%)' 
                    : 'Final Amount (No GST)'
                  }
                </div>
              </div>

              {/* Paid Amount */}
              <div>
                <label htmlFor="paidAmount" className="block text-sm font-medium text-gray-700 mb-2">
                  Paid Amount (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={(parseFloat(formData.amount || 0) + parseFloat(formData.taxAmount || 0))}
                  id="paidAmount"
                  name="paidAmount"
                  value={formData.paidAmount || formData.initialPayment}
                  onChange={handleChange}
                  className="input text-center text-lg font-semibold text-green-600"
                  placeholder="Enter paid amount"
                />
                <div className="text-xs text-gray-500 mt-1 text-center">
                  How much actually paid
                </div>
              </div>

              {/* Remaining Amount - Auto calculated */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remaining Amount (₹)
                </label>
                <div className="input text-center text-lg font-semibold text-orange-600 bg-gray-50">
                  ₹{parseFloat(
                    ((parseFloat(formData.amount || 0) + parseFloat(formData.taxAmount || 0)) - 
                     parseFloat(formData.paidAmount || formData.initialPayment || 0))
                  ).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1 text-center">
                  Total Amount - Paid Amount
                </div>
              </div>
            </div>

                         {/* Payment Status Badge */}
             <div className="flex items-center justify-center space-x-2 mb-4">
               <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                 (() => {
                   const totalAmt = parseFloat(formData.amount || 0) + parseFloat(formData.taxAmount || 0)
                   const paidAmt = parseFloat(formData.paidAmount || formData.initialPayment || 0)
                   
                   // Check if fully paid first
                   if (totalAmt > 0 && Math.abs(paidAmt - totalAmt) < 0.01) {
                     return 'bg-green-100 text-green-800'
                   }
                   // Check if partially paid
                   if (paidAmt > 0 && paidAmt < totalAmt) {
                     return 'bg-blue-100 text-blue-800'
                   }
                   // Default to pending
                   return 'bg-orange-100 text-orange-800'
                 })()
               }`}>
                 {(() => {
                   const totalAmt = parseFloat(formData.amount || 0) + parseFloat(formData.taxAmount || 0)
                   const paidAmt = parseFloat(formData.paidAmount || formData.initialPayment || 0)
                   
                   // Check if fully paid first
                   if (totalAmt > 0 && Math.abs(paidAmt - totalAmt) < 0.01) {
                     return <><CheckCircle className="h-4 w-4 mr-1" />Fully Paid</>
                   }
                   // Check if partially paid
                   if (paidAmt > 0 && paidAmt < totalAmt) {
                     return <><CreditCard className="h-4 w-4 mr-1" />Partially Paid</>
                   }
                   // Default to pending
                   return <><XCircle className="h-4 w-4 mr-1" />Payment Pending</>
                 })()}
               </span>
             </div>

             {/* Payment Status Description */}
             <div className="mb-4">
               <label htmlFor="paymentStatusDescription" className="block text-sm font-medium text-gray-700 mb-2">
                 Payment Status Description
               </label>
               <textarea
                 id="paymentStatusDescription"
                 name="paymentStatusDescription"
                 rows={3}
                 value={formData.paymentStatusDescription}
                 onChange={handleChange}
                 className="input resize-none focus:ring-blue-500 focus:border-blue-500"
                 placeholder="Add details about payment status, payment terms, or any payment-related notes..."
               />
               <p className="text-xs text-gray-500 mt-1">
                 Optional: Add payment terms, due dates, or any payment-related information
               </p>
             </div>

            {/* Payment History */}
            {workEntry && workEntry.payments && workEntry.payments.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Recent Payments</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {workEntry.payments.slice(-3).map((payment) => (
                    <div key={payment.id} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        {new Date(payment.date).toLocaleDateString()} • {payment.method}
                      </span>
                      <span className="font-medium text-green-600">
                        +₹{parseFloat(payment.amount).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
                {workEntry.payments.length > 3 && (
                  <div className="text-xs text-gray-500 mt-2">
                    And {workEntry.payments.length - 3} more payments...
                  </div>
                )}
              </div>
            )}

            {/* Payment Details */}
            {!workEntry || !workEntry.id ? (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Payment Details (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Payment Method */}
                  <div>
                    <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method
                    </label>
                    <select
                      id="paymentMethod"
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cheque">Cheque</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                    </select>
                  </div>

                  {/* Payment Notes */}
                  <div>
                    <label htmlFor="paymentNotes" className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Notes
                    </label>
                    <input
                      type="text"
                      id="paymentNotes"
                      name="paymentNotes"
                      value={formData.paymentNotes}
                      onChange={handleChange}
                      className="input"
                      placeholder="Add payment details or reference"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-3 text-xs text-gray-500 text-center">
                To manage payments, save this entry and use the payment button in Work Management
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
          Additional Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          rows={3}
          value={formData.notes}
          onChange={handleChange}
          className="input resize-none"
          placeholder="Any additional notes or comments"
        />
      </div>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary flex items-center justify-center space-x-2"
        >
          <X className="h-4 w-4" />
          <span>Cancel</span>
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>{workEntry ? 'Update Work Entry' : 'Save Work Entry'}</span>
            </>
          )}
        </button>
       </div>
       </form>
     </div>
   )
 }

export default WorkEntryForm
